<template>
    <li v-if="show" class="module-list-entry" :class="{ 'module-list-entry-small': small }" :style="themeModuleStyle">
        <component
            :is="showButtons ? 'div' : 'button'"
            class="module-card"
            :class="[`module-card-${module.value}`, { 'module-card-disabled': !module.enabled, 'module-card-small': small }]"
            :type="showButtons ? null : 'button'"
            @click="moduleClicked">
            <div class="module-card-top">
                <div class="module-card-icon" :class="{ disabled: !module.enabled }">
                    <template v-if="module.value === 'ordering-table'">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="30"
                            height="30"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-hand-platter-icon lucide-hand-platter">
                            <path d="M12 3V2" />
                            <path
                                d="m15.4 17.4 3.2-2.8a2 2 0 1 1 2.8 2.9l-3.6 3.3c-.7.8-1.7 1.2-2.8 1.2h-4c-1.1 0-2.1-.4-2.8-1.2l-1.302-1.464A1 1 0 0 0 6.151 19H5" />
                            <path d="M2 14h12a2 2 0 0 1 0 4h-2" />
                            <path d="M4 10h16" />
                            <path d="M5 10a7 7 0 0 1 14 0" />
                            <path d="M5 14v6a1 1 0 0 1-1 1H2" />
                        </svg>
                    </template>
                    <template v-else>
                        <feather class="icon w-auto h-auto" :type="module.icon" size="28" />
                    </template>
                </div>
                <span v-if="!small" class="module-card-status" :class="{ disabled: !module.enabled }">
                    {{ module.enabled ? "Actif" : "À activer" }}
                </span>
            </div>

            <div class="module-card-body">
                <h5>
                    {{ getModuleTypeLabel(module.value) }}
                    <newBadge class="ms-2" v-if="hasNewBadge" />
                </h5>
                <p v-if="module.value === 'ordering-table' && !module.enabled" class="module-card-alert">
                    <feather class="icon w-auto h-auto me-1" type="alert-circle" size="13" />
                    {{ $tl("labels.modules.orderingTable.disabled") }}
                </p>
                <p v-else-if="!small && module.description" class="module-card-description">
                    {{ module.description }}
                </p>
            </div>

            <div v-if="showButtons && (module.value !== 'ordering-table' || module.enabled)" class="module-card-actions">
                <a v-if="isYproxModule" :href="module.goToUrl" target="_blank" class="btn btn-primary btn-circle btn-sm">
                    {{ $tl("labels.modules.actions.goTo") }}
                </a>
                <template v-else>
                    <router-link v-if="module.enabled" :to="module.goToUrl" class="btn btn-primary btn-circle btn-sm">
                        {{ $tl("labels.modules.actions.goTo") }}
                    </router-link>
                    <template v-else>
                        <router-link v-if="isNoShow" :to="module.activateUrl" class="btn btn-primary btn-circle btn-sm">
                            {{ $tl("labels.modules.actions.enable") }}
                        </router-link>
                        <button v-else type="button" class="btn btn-more-infos btn-circle btn-sm" @click.prevent="canDisplayModal = true">
                            {{ $tl("labels.subscriptions.moreInfos") }}
                        </button>
                    </template>
                </template>
            </div>
        </component>
        <ask-for-subscription v-if="canDisplayModal" :module="module.value" @close="canDisplayModal = false" />
    </li>
</template>
<script>
import newBadge from "../NewBadges/newBadge";
import ModuleTypesEnum from "../../mixins/enums/ModuleTypesEnum";
import AskForSubscription from "../Modals/subscriptions/AskForSubscription.vue";

export default {
    name: "ModuleListItem",
    props: {
        module: {
            required: true,
        },
        showButtons: {
            default: true,
            type: Boolean,
        },
        small: {
            default: false,
        },
    },
    components: { newBadge, AskForSubscription },
    data() {
        return {
            canDisplayModal: false,
        };
    },
    mixins: [ModuleTypesEnum],
    computed: {
        show() {
            if (this.isYservices && !this.module.enabled && !this.showButtons) {
                return false;
            }

            return true;
        },
        isYproxModule() {
            return this.isYservices && this.module.value === this.MODULE_TYPE_WEBSITE.value;
        },
        hasNewBadge() {
            return this.checkForExistingNewBadge(this.module.value.replace(/-/g, "_"));
        },
        themeModuleStyle() {
            return {
                "--theme-module-card-background": this.$style["module-card-background"] || "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
                "--theme-module-card-border-rgb": this.$style["module-card-border-rgb"] || "8, 62, 96",
                "--theme-module-card-line-rgb": this.$style["module-card-line-rgb"] || "8, 62, 96",
                "--theme-module-icon-color": this.$style["module-icon-color"] || "#ffffff",
                "--theme-module-icon-background": this.$style["module-icon-background"] || "linear-gradient(145deg, #083e60, #0b5d87)",
                "--theme-module-icon-border-color": this.$style["module-icon-border-color"] || "transparent",
                "--theme-module-icon-shadow-rgb": this.$style["module-icon-shadow-rgb"] || "8, 62, 96",
                "--theme-module-action-shadow-rgb": this.$style["module-action-shadow-rgb"] || "8, 62, 96",
            };
        },
    },
    methods: {
        moduleClicked() {
            if (this.showButtons) {
                return;
            }

            const url = this.module.enabled ? this.module.goToUrl : this.module.activateUrl;

            if (typeof url === "string") {
                window.open(url, "_blank");

                this.$emit("clicked-on-same-module");

                return;
            }

            if (url.name === this.$route.name) {
                this.$emit("clicked-on-same-module");
            } else {
                const desiredModule = (url.name || "").split(".")[0];
                const currentModule = (this.$route.name || "").split(".")[0];

                this.$router.push(url);

                if (desiredModule === currentModule) {
                    this.$emit("clicked-on-same-module");
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.module-list-entry {
    min-width: 0;
    --theme-module-card-background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
    --theme-module-card-border-rgb: 8, 62, 96;
    --theme-module-card-line-rgb: 8, 62, 96;
    --theme-module-icon-color: #ffffff;
    --theme-module-icon-background: linear-gradient(145deg, #083e60, #0b5d87);
    --theme-module-icon-border-color: transparent;
    --theme-module-icon-shadow-rgb: 8, 62, 96;
    --theme-module-action-shadow-rgb: 8, 62, 96;
}

.module-card {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 210px;
    width: 100%;
    height: 100%;
    padding: 22px;
    overflow: hidden;
    color: #202834;
    text-align: left;
    background: var(--theme-module-card-background);
    border: 1px solid rgba(var(--theme-module-card-border-rgb), 0.11);
    border-radius: 16px;
    box-shadow: 0 18px 40px rgba(var(--theme-module-card-border-rgb), 0.07);
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease,
        transform 0.18s ease;
}

.module-card::before {
    position: absolute;
    top: 0;
    right: 18px;
    left: 18px;
    height: 2px;
    content: "";
    background: linear-gradient(
        90deg,
        rgba(var(--theme-module-card-line-rgb), 0),
        rgba(var(--theme-module-card-line-rgb), 0.28),
        rgba(var(--theme-module-card-line-rgb), 0)
    );
    opacity: 0;
    transition: opacity 0.18s ease;
}

button.module-card {
    cursor: pointer;
}

.module-card:hover {
    border-color: rgba(var(--theme-module-card-border-rgb), 0.22);
    box-shadow: 0 22px 54px rgba(var(--theme-module-card-border-rgb), 0.12);
    transform: translateY(-3px);
}

.module-card:hover::before {
    opacity: 1;
}

.module-card-disabled {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.module-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.module-card-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    flex: 0 0 52px;
    color: var(--theme-module-icon-color);
    background: var(--theme-module-icon-background);
    border: 1px solid var(--theme-module-icon-border-color);
    border-radius: 14px;
    box-shadow: 0 14px 28px rgba(var(--theme-module-icon-shadow-rgb), 0.2);
}

.module-card-icon.disabled,
.module-card-disabled .module-card-icon {
    color: #8a97a8;
    background: #eef3f7;
    box-shadow: none;
}

.module-card-status {
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    padding: 4px 10px;
    color: #11624d;
    font-size: 12px;
    font-weight: 800;
    line-height: 1;
    white-space: nowrap;
    background: #e7f6f1;
    border: 1px solid rgba(17, 98, 77, 0.08);
    border-radius: 999px;
}

.module-card-status.disabled {
    color: #7a5b11;
    background: #fff5dc;
    border-color: rgba(122, 91, 17, 0.1);
}

.module-card-body {
    margin-top: 20px;
}

.module-card-body h5 {
    display: flex;
    align-items: center;
    margin: 0;
    color: #1d2939;
    font-size: 18px;
    font-weight: 800;
    line-height: 1.25;
}

.module-card-description,
.module-card-alert {
    min-height: 42px;
    margin: 10px 0 0;
    color: #667085;
    font-size: 13px;
    line-height: 1.5;
}

.module-card-alert {
    display: flex;
    align-items: flex-start;
    color: #c54045;
}

.module-card-actions {
    display: flex;
    justify-content: flex-start;
    margin-top: 20px;
}

.module-card-actions .btn {
    padding: 7px 14px;
    font-size: 12px;
    font-weight: 800;
    border-radius: 999px;
    box-shadow: 0 10px 20px rgba(var(--theme-module-action-shadow-rgb), 0.12);
}

.module-card-small {
    min-height: 0;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 14px;
    padding: 0;
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
}

.module-card-small::before {
    display: none;
}

.module-card-small:hover {
    box-shadow: none;
    transform: none;
}

.module-card-small .module-card-top {
    flex: 0 0 auto;
}

.module-card-small .module-card-icon {
    width: 48px;
    height: 48px;
    flex-basis: 48px;
}

.module-card-small .module-card-body {
    margin-top: 0;
}

.module-card-small .module-card-body h5 {
    font-size: 14px;
}

.module-list-entry-small + .module-list-entry-small {
    margin-top: 14px;
}

@media (max-width: 767px) {
    .module-card {
        min-height: 0;
        padding: 18px;
    }

    .module-card-description,
    .module-card-alert {
        min-height: 0;
    }
}
</style>
